import Amplify, { Auth } from 'aws-amplify';
import { CognitoAccessToken, CognitoIdToken, CognitoUserSession } from 'amazon-cognito-identity-js';
import { SITE_HOST, STAGE } from './app-global-values';

import { isExpired } from './jwt-utils';

const MIDWAY_PROVIDER = 'Midway';
const USERNAME_REMOVE_SUFFIX = '@amazon.com';

const AMPLIFY_POOL_ID_MAP = {
  beta: 'eu-west-1_O85WsykSl',
  gamma: 'eu-west-1_lCwQ4Yg9o',
  prod: 'eu-west-1_i5Zup6oeF',
};

const AMPLIFY_CLIENT_ID_MAP = {
  beta: '7k36uidqtougqsl3uq280jon33',
  gamma: '4b1g8egnucvkqo7q7p6ihe7lrd',
  prod: '2r3drp109lla80gec0a7o513ug',
};

const AMPLIFY_REGION = 'eu-west-1';
const AMPLIFY_POOL_ID = AMPLIFY_POOL_ID_MAP[STAGE];
const AMPLIFY_CLIENT_ID = AMPLIFY_CLIENT_ID_MAP[STAGE];
const AMPLIFY_COGNITO_DOMAIN = `auth.${STAGE}.rosetta.amazon.dev`;
const AMPLIFY_SCOPE = ['openid'];
const AMPLIFY_RESPONSE_TYPE = 'code';

const AMPLIFY_CONFIG = {
  Auth: {
    mandatorySignId: true,
    region: AMPLIFY_REGION,
    userPoolId: AMPLIFY_POOL_ID,
    userPoolWebClientId: AMPLIFY_CLIENT_ID,
  },
  oauth: {
    domain: AMPLIFY_COGNITO_DOMAIN,
    redirectSignIn: SITE_HOST,
    redirectSignOut: SITE_HOST,
    scope: AMPLIFY_SCOPE,
    responseType: AMPLIFY_RESPONSE_TYPE,
  },
};

Amplify.configure(AMPLIFY_CONFIG);

// Store a user profile from Amplify's currentAuthenticatedUser method
class UserProfile {
  static authProfile = null;
  static jwtPromise = null;

  /*
   * This func is responsible for resolving user profile initially, upon entering the website and also
   * in case of tokens expiration.
   */
  static authenticate() {
    return new Promise((resolve, reject) => {
      if (this.authProfile) {
        resolve(this.authProfile);
      } else {
        Auth.currentAuthenticatedUser()
          .then(user => {
            this.authProfile = user;
            resolve(user);
          })
          .catch(error => {
            Auth.federatedSignIn({ provider: MIDWAY_PROVIDER })
              .then(val => {
                return Auth.currentAuthenticatedUser();
              })
              .then(user => {
                this.authProfile = user;
                resolve(user);
              })
              .catch(e => reject(e));
          });
      }
    });
  }

  static getUserInformation() {
    return this.authenticate()
      .then(profile => {
        const username = profile.username.substr(0, profile.username.indexOf(USERNAME_REMOVE_SUFFIX));
        const fullName = profile.username;
        const jobTitle = profile.attributes?.profile;

        return {
          username: username,
          fullName: fullName,
          jobTitle: jobTitle,
        };
      })
      .catch(e => {
        console.log(e);
      });
  }

  /*
   * This f is responsible for resolving JWT that is used for all requests to the backend.
   * Interestingly, access token is rejected by cognito authorizer, so id token is used instead.
   * Local jwtPromise variable is used to avoid multiple calls to refresh the session.
   * Session is refreshed manually, because for some reason amplify's profile.refreshSession doesn't
   * refresh the id token.
   */
  static async getJwtToken() {
    let jwt = null;
    if (this.jwtPromise !== null) {
      jwt = await this.jwtPromise;
    }

    if (jwt === null || isExpired(jwt, false)) {
      const newPromise = this.authenticate().then(profile => {
        const session = profile.getSignInUserSession();
        const idToken = session.getIdToken().jwtToken;
        const clientId = profile.pool.getClientId();

        if (isExpired(idToken, false)) {
          // have to check for expiration again
          return MidwayIdSessionRefresh.getNewSession(session, clientId).then(newSession => {
            profile.setSignInUserSession(newSession);
            return newSession.getIdToken().jwtToken;
          });
        } else {
          return idToken;
        }
      });
      this.jwtPromise = newPromise;
      return newPromise;
    } else {
      return Promise.resolve(jwt);
    }
  }
}

/*
 * This class is responsible for making request to cognito to get the new set of tokens.
 * Both access and id tokens are refreshed. Content-Type is application/x-amz-json-1.1 because it seems that
 * the endpoint is implemented with coral. Also, the user agent is fake here to trick cognito into thinking
 * it's done using aplify lib.
 */
class MidwayIdSessionRefresh {
  static getNewSession(oldSession, clientId) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://cognito-idp.eu-west-1.amazonaws.com/');
      xhr.withCredentials = false;
      xhr.setRequestHeader('x-amz-target', 'AWSCognitoIdentityProviderService.InitiateAuth');
      xhr.setRequestHeader('x-amz-user-agent', 'aws-amplify/0.1.x js');
      xhr.setRequestHeader('Content-Type', 'application/x-amz-json-1.1');

      const body = {
        ClientId: clientId,
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: {
          DEVICE_KEY: null,
          REFRESH_TOKEN: oldSession.getRefreshToken().token,
        },
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);

          const newAccessToken = new CognitoAccessToken({
            AccessToken: response.AuthenticationResult.AccessToken,
          });
          const newIdToken = new CognitoIdToken({
            IdToken: response.AuthenticationResult.IdToken,
          });
          const newSession = new CognitoUserSession({
            IdToken: newIdToken,
            AccessToken: newAccessToken,
            RefreshToken: oldSession.getRefreshToken(),
          });
          resolve(newSession);
        } else {
          reject(new Error(`Failed to get new session: ${xhr}`));
        }
      };

      // closes the promise in the case of an error
      xhr.onerror = reject;
      xhr.send(JSON.stringify(body));
    });
  }
}

if (process.env.NODE_ENV !== 'production') {
  UserProfile = class FakeUserProfile {
    static authenticate() {}
    static getUserInformation() {
      return Promise.resolve({
        username: 'useralias',
        fullName: 'useralias@amazon.com',
        jobTitle: 'Super Cool and Long Job Title',
      });
    }
    static getJwtToken() {
      // Fake token that expires in 150 y and contains some tenants + RTS_ADMINS
      return Promise.resolve(
        'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1c2VybmFtZSIsImNvZ25pdG86Z3JvdXBzIjpbIlJUU19BRE1JTlMiLCJ3b3JrZmxvd19tYW5hZ2VyQFRFU1RfVEVOQU5UXzEiLCJhZG1pbkBURVNUX1RFTkFOVF8yIiwidXNlckBURVNUX1RFTkFOVF8xIiwidXNlckBURVNUX1RFTkFOVF8yIl0sImNvZ25pdG86cm9sZXMiOlsiYXJuOmF3czppYW06OmFjY291bnQ6cm9sZS9URVNUX1RFTkFOVF8xL0FjY2Vzc1JvbGVAVEVTVF9URU5BTlRfMSIsImFybjphd3M6aWFtOjphY2NvdW50OnJvbGUvVEVTVF9URU5BTlRfMi9BY2Nlc3NSb2xlQFRFU1RfVEVOQU5UXzIiXSwiaWF0IjoxNjM3OTU1MTk1LCJleHAiOjYzNzE1OTUxOTV9.PqTvPXu5krkC0wC6q-LIYfutUCpU5FoZ1wx59NK9Kl0SFl_W5ahTF_70eec3q-15YV5hmpQDAwwP8eYMNoey8oNAbFVumiY_338V0xWuxGWxABTJ_FCLJ4FWhou3WB_dWmCldkPqqFdLME_RrfYAQg2rUkCoSsvkRwdzy5IKeHd1o6bbF0I9fUDsyEVcFgJJYq1sPNepvSkmyq-R_ydjKnLGJBGb-ZFXtSkcnVOooQkc628nSIHPCY297es1F4R5x6UxAGzWkcTd26zGDj3ZqStF1LCIzwi3XkjhaEhuhTWPrI-eoAbgjDudLwfM9fqktCcHhxX04vEH4_S9o-GWSQ'
      );
      // Fake token without RTS_ADMINS
      //return Promise.resolve('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjb2duaXRvOmdyb3VwcyI6WyJUZW5hbnRVc2VyQFRFU1RfVEVOQU5UXzEiLCJUZW5hbnRBZG1pbkBURVNUX1RFTkFOVF8yIl0sImlhdCI6MTUxNjIzOTAyMiwiZXhwIjo5OTk5OTk5OTk5fQ.iGx06x_J5byVHyBNLP8PBc16_MWfeBbt8qRHXOgjStg');
      // Fake token with just RTS_ADMINS
      //return Promise.resolve('eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1c2VybmFtZSIsImNvZ25pdG86Z3JvdXBzIjpbIlJUU19BRE1JTlMiXSwiY29nbml0bzpyb2xlcyI6WyJhcm46YXdzOmlhbTo6YWNjb3VudDpyb2xlL1RFU1RfVEVOQU5UXzEvQWNjZXNzUm9sZUBURVNUX1RFTkFOVF8xIiwiYXJuOmF3czppYW06OmFjY291bnQ6cm9sZS9URVNUX1RFTkFOVF8yL0FjY2Vzc1JvbGVAVEVTVF9URU5BTlRfMiJdLCJpYXQiOjE2Mzc5NTUxOTUsImV4cCI6NjM3MTU5NTE5NX0.HM7k15TN3H5LpTk5I00rsodRsl7jq67skvwGn6FQzskwRhjqzYu4VNhp24eK92qkKAN8qIE-Wt17cMgkpQkweyS9F3Kx9OWQ8owXj_MAPuwnFAyLXEZKPDbH2U30M4uNeHmAdYpZ3ruU9lgBCS337sEBLB2AaInq8a5rCwhahE1ONJqtx7KyS5typeyJntR5lpSp4vhJuNRfoGPSd73EQNVwrUO2I4ZJWkMFPwXx6PtLSsiR1t801P_cSuxZ8sh7DmO4QpC_NlfEQuCkGdMP33Xb28lPE_43VTxxdBdMFm1vxPzpMt5zE5p7srSkokcqGoCB170471XsoAPrGN5ysg');
    }
  };
}

export default UserProfile;
